import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import api from '../services/api.js';
import { filterNav, canAccessSlug, userTitles } from '../config/access.js';
import { menuData } from './menuData.js';
import BrandLogo from '../components/BrandLogo.jsx';
import useIsPhone from '../hooks/useIsPhone.js';
import LanguageToggle from '../components/LanguageToggle.jsx';
import HelpButton from '../components/help/HelpButton.jsx';

// group/key map to adminShell locale keys (t(`adminShell.groups.${group}`) and
// t(`adminShell.nav.${key}`)); slug stays the route/access identifier.
const ADMIN_NAV = [
  {
    group: 'overview',
    items: [
      { slug: 'admin-dashboard', key: 'adminDashboard', icon: 'fa-solid fa-chart-line' },
    ],
  },
  {
    group: 'operations',
    items: [
      { slug: 'referral-queue', key: 'referrals', icon: 'fa-solid fa-rectangle-list' },
      { slug: 'connector-accreditation-management', key: 'accreditationReview', icon: 'fa-solid fa-id-card' },
      { slug: 'commission-processing-queue', key: 'commissionProcessing', icon: 'fa-solid fa-calculator' },
    ],
  },
  {
    group: 'finance',
    items: [
      { slug: 'export-handoff-management', key: 'exportHandoff', icon: 'fa-solid fa-file-export' },
    ],
  },
  {
    group: 'administration',
    items: [
      { slug: 'commission-configuration-approvals', key: 'commissionPolicies', icon: 'fa-solid fa-percent' },
      { slug: 'user-administration-security', key: 'userDirectory', icon: 'fa-solid fa-user-shield' },
      { slug: 'relationship-managers', key: 'relationshipManagers', icon: 'fa-solid fa-user-tie' },
      { slug: 'branch-management', key: 'branchMaintenance', icon: 'fa-solid fa-code-branch' },
      { slug: 'role-permission-management', key: 'rolePermissions', icon: 'fa-solid fa-shield-halved' },
      { slug: 'notification-management', key: 'notifications', icon: 'fa-solid fa-bell' },
      { slug: 'branding-management', key: 'branding', icon: 'fa-solid fa-palette' },
      { slug: 'integrations-management', key: 'integrations', icon: 'fa-solid fa-plug' },
      { slug: 'reports-management', key: 'reports', icon: 'fa-solid fa-chart-column' },
      { slug: 'audit-logs-security-monitoring', key: 'securityLogs', icon: 'fa-solid fa-receipt' },
    ],
  },
];

// Route (first path segment) -> { translation key, icon } for the top-bar title.
// Sidebar items plus alias/detail routes that don't have their own nav entry.
const PAGE_TITLES = {
  ...Object.fromEntries(
    ADMIN_NAV.flatMap((g) => g.items.map((i) => [i.slug, { tkey: `adminShell.nav.${i.key}`, icon: i.icon }]))
  ),
  'referral-details': { tkey: 'adminShell.titles.referralDetails', icon: 'fa-solid fa-rectangle-list' },
  'connector-accreditation': { tkey: 'adminShell.nav.accreditationReview', icon: 'fa-solid fa-id-card' },
  'connector-accreditation-management': { tkey: 'adminShell.nav.accreditationReview', icon: 'fa-solid fa-id-card' },
  'commission-processing': { tkey: 'adminShell.nav.commissionProcessing', icon: 'fa-solid fa-calculator' },
  'commission-configuration': { tkey: 'adminShell.nav.commissionPolicies', icon: 'fa-solid fa-percent' },
  'export-handoff': { tkey: 'adminShell.nav.exportHandoff', icon: 'fa-solid fa-file-export' },
};

// Drill-down segments get a back link in the topbar — inline with the logo/title
// area rather than buried inside the page body (QA 8205). Keyed by first path
// segment, same as PAGE_TITLES; the label tkey must exist in both locales.
// QA 8293 re-open: the Referral Details back link first shipped inside the page
// body, which is not where this app puts drill-down navigation. Same treatment
// as reports — upper-left of the top bar, ahead of the page title. `when` gates
// it on reaching the destination, so a role that can open a referral but not the
// queue is not offered a link into a page it would be bounced out of.
const TOPBAR_BACK = {
  reports: { to: '/reports-management', tkey: 'reports.drillBack' },
  'referral-details': {
    to: '/referral-queue',
    tkey: 'referralDetails.backToList',
    when: (u) => canAccessSlug(u, 'referral-queue'),
  },
};

// Routes whose page BODY is fully translated. The Fil/En toggle only shows here,
// so it never appears on English-only pages where switching would do nothing.
const TRANSLATED_SEGMENTS = new Set([
  'admin-dashboard',
  'referral-queue',
  'referral-details',
  'commission-processing',
  'commission-processing-queue',
]);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const seg = useMemo(
    () => location.pathname.replace(/^\//, '').split('/')[0],
    [location.pathname]
  );
  const pageTitle = PAGE_TITLES[seg] || null;
  const backEntry = TOPBAR_BACK[seg] || null;
  const topbarBack = backEntry && (!backEntry.when || backEntry.when(user)) ? backEntry : null;
  const showLangToggle = TRANSLATED_SEGMENTS.has(seg);
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The sidebar starts collapsed on phones (QA 7990). Expanded it covers most
  // of a phone screen, so every page load opened with the content hidden
  // behind a drawer the user had to dismiss by hand before doing anything.
  const isPhone = useIsPhone();
  const [sidebarOpen, setSidebarOpen] = useState(!isPhone);
  // Also collapse when the viewport crosses into phone territory (rotation,
  // window resize) — otherwise a desktop-width session that narrows keeps the
  // drawer pinned open over the content.
  useEffect(() => {
    if (isPhone) setSidebarOpen(false);
  }, [isPhone]);
  const [inbox, setInbox] = useState({ unread: 0, items: [] });

  useEffect(() => {
    document.body.classList.add('admin-page');
    return () => document.body.classList.remove('admin-page');
  }, []);

  // In-app notification feed (QA 7747/7750/7759): load on mount, refresh on
  // route change and every 60s so workflow hand-offs surface without a reload.
  useEffect(() => {
    let active = true;
    const load = () => api.myNotifications()
      .then((d) => { if (active && d) setInbox(d); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 60_000);
    return () => { active = false; clearInterval(timer); };
  }, [location.pathname]);

  const openNotifications = () => {
    if (inbox.unread > 0) {
      api.markNotificationsRead()
        .then(() => setInbox((s) => ({ ...s, unread: 0, items: s.items.map((n) => ({ ...n, read: true })) })))
        .catch(() => {});
    }
  };

  const allowedSlugs = new Set(
    filterNav(menuData.items, user).flatMap((item) =>
      item.children ? item.children.map((c) => c.slug) : [item.slug]
    )
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = (user?.name || user?.email || 'A')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div className={`admin-shell${sidebarOpen ? ' admin-shell--open' : ' admin-shell--collapsed'}`}>
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__header">
          <Link to="/" className="admin-sidebar__brand">
            <BrandLogo size="sm" variant="light" />
          </Link>
          <button
            type="button"
            className="admin-sidebar__toggle btn btn-link text-white p-0 border-0"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={sidebarOpen ? t('adminShell.topbar.collapseSidebar') : t('adminShell.topbar.expandSidebar')}
          >
            <i className={`fa-solid ${sidebarOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`} />
          </button>
        </div>

        <nav className="admin-sidebar__nav" aria-label="Admin navigation">
          {ADMIN_NAV.map(({ group, items }) => {
            const visible = items.filter((item) => allowedSlugs.has(item.slug));
            if (!visible.length) return null;
            return (
              <div className="admin-sidebar__group" key={group}>
                <span className="admin-sidebar__group-label">{t(`adminShell.groups.${group}`)}</span>
                {visible.map((item) => {
                  const label = t(`adminShell.nav.${item.key}`);
                  return (
                    <NavLink
                      key={item.slug}
                      to={`/${item.slug}`}
                      className={({ isActive }) =>
                        `admin-sidebar__item${isActive ? ' active' : ''}`
                      }
                      title={sidebarOpen ? undefined : label}
                    >
                      <i className={`${item.icon} admin-sidebar__icon`} aria-hidden="true" />
                      <span className="admin-sidebar__label">{label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="admin-sidebar__footer">
          <div className="admin-sidebar__user">
            <span className="admin-sidebar__avatar">
              {initials}
            </span>
            <div className="admin-sidebar__user-info">
              <span className="admin-sidebar__user-name">{user?.name || user?.email}</span>
              <span className="admin-sidebar__user-role">{userTitles(user).join(' + ') || user?.role}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="admin-main">
        {/* Top bar */}
        <header className="admin-topbar">
          <button
            type="button"
            className="btn btn-link p-0 border-0 d-lg-none text-secondary me-3"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={t('adminShell.topbar.toggleSidebar')}
          >
            <i className="fa-solid fa-bars fs-5" aria-hidden="true" />
          </button>

          {topbarBack && (
            <Link to={topbarBack.to} className="admin-topbar__back" data-testid="topbar-back">
              <i className="fa-solid fa-arrow-left me-2" aria-hidden="true" />
              {t(topbarBack.tkey)}
            </Link>
          )}

          {/* Current page title — uses the otherwise-empty top-bar space so pages
              don't each need a tall header card. */}
          {pageTitle && (
            <div className="admin-topbar__title d-flex align-items-center gap-2">
              <i className={`${pageTitle.icon} text-primary`} aria-hidden="true" />
              <span className="fw-bold">{t(pageTitle.tkey)}</span>
            </div>
          )}

          <div className="ms-auto d-flex align-items-center gap-3">
            <HelpButton variant="dark" />
            {showLangToggle && <LanguageToggle variant="dark" />}

            <div className="dropdown">
              <button
                type="button"
                className="btn btn-link position-relative text-secondary p-0 border-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label="Notifications"
                data-testid="notification-bell"
                onClick={openNotifications}
              >
                <i className="fa-solid fa-bell fs-5" aria-hidden="true" />
                {inbox.unread > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: 10 }}
                    data-testid="notification-badge"
                  >
                    {inbox.unread > 9 ? '9+' : inbox.unread}
                  </span>
                )}
              </button>
              <div className="dropdown-menu dropdown-menu-end p-0" style={{ minWidth: '20rem', maxWidth: '24rem' }} data-testid="notification-panel">
                <h6 className="dropdown-header pt-3">{t('adminShell.topbar.notifications')}</h6>
                {inbox.items.length === 0 ? (
                  <p className="text-muted small mb-0 px-3 pb-3">{t('adminShell.topbar.noNotifications')}</p>
                ) : (
                  <div style={{ maxHeight: '22rem', overflowY: 'auto' }}>
                    {inbox.items.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className={`dropdown-item text-wrap py-2${n.read ? '' : ' fw-semibold'}`}
                        onClick={() => n.link && navigate(n.link)}
                      >
                        <span className="d-block small">{n.title}</span>
                        {n.body && <span className="d-block small text-muted">{n.body}</span>}
                        <span className="d-block text-muted" style={{ fontSize: 11 }}>
                          {new Date(n.createdAt).toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="dropdown">
              <button
                type="button"
                className="btn btn-link text-secondary text-decoration-none d-flex align-items-center gap-2 p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-testid="user-menu"
              >
                <span
                  className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold"
                  style={{ width: 32, height: 32, fontSize: 12 }}
                >
                  {initials}
                </span>
                <span className="d-none d-lg-inline small fw-semibold">
                  {user?.name || user?.email}
                </span>
                <i className="fa-solid fa-chevron-down small" aria-hidden="true" />
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <span className="dropdown-item-text small text-muted">{user?.email}</span>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item" type="button" onClick={handleLogout} data-testid="logout-button">
                    <i className="fa-solid fa-right-from-bracket me-2" aria-hidden="true" />
                    {t('adminShell.topbar.signOut')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
